import { useState, useEffect } from "react";
import { Send, CheckCircle2, Copy, Check, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

export default function UserAccount() {
  const { user } = useAuth();
  const [tg, setTg] = useState(null);
  const [deepLink, setDeepLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadStatus = () => api.get("/telegram/status").then(({ data }) => setTg(data)).catch(() => setTg(null));
  useEffect(() => { loadStatus(); }, []);

  const genLink = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/telegram/link-token");
      if (data.deep_link) {
        setDeepLink(data.deep_link);
        window.open(data.deep_link, "_blank");
      } else {
        toast.error("Bot indisponível no momento. Tente novamente em instantes.");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(deepLink); setCopied(true); toast.success("Link copiado!"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Não foi possível copiar."); }
  };

  return (
    <div className="max-w-lg space-y-6" data-testid="user-account">
      <h2 className="font-heading text-xl font-bold tracking-tight sm:text-2xl">Dados da conta</h2>

      <div className="space-y-4 rounded-lg border border-border bg-card p-6">
        <div className="space-y-2">
          <Label>Nome</Label>
          <Input value={user?.name || ""} readOnly data-testid="account-name" />
        </div>
        <div className="space-y-2">
          <Label>E-mail</Label>
          <Input value={user?.email || ""} readOnly data-testid="account-email" />
        </div>
        <div className="space-y-2">
          <Label>WhatsApp</Label>
          <Input value={user?.whatsapp || "—"} readOnly data-testid="account-whatsapp" />
        </div>
        <div className="space-y-2">
          <Label>Tipo de conta</Label>
          <Input value={user?.role || ""} readOnly className="capitalize" data-testid="account-role" />
        </div>
      </div>

      {/* Telegram linking */}
      <div className="space-y-4 rounded-lg border border-border bg-card p-6" data-testid="telegram-link-card">
        <div className="flex items-center gap-2">
          <Send className="h-5 w-5 text-primary" />
          <h3 className="font-heading text-lg font-semibold">Telegram</h3>
        </div>

        {tg?.linked ? (
          <div className="flex items-center gap-3 rounded-lg border border-green-500/30 bg-green-500/10 p-4" data-testid="telegram-linked">
            <CheckCircle2 className="h-6 w-6 text-green-500" />
            <div>
              <p className="font-medium">Conta vinculada</p>
              <p className="text-sm text-muted-foreground">
                {tg.telegram_username ? `@${tg.telegram_username}` : `ID ${tg.telegram_id}`} · saldo, créditos e histórico unificados.
              </p>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Vincule o bot do Telegram à sua conta para unificar <b>saldo, créditos e histórico</b> nos dois lugares.
            </p>
            <Button onClick={genLink} disabled={loading} className="gap-2 glow-red" data-testid="telegram-link-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Vincular Telegram
            </Button>
            {deepLink && (
              <div className="space-y-2">
                <Label>Ou abra este link no Telegram</Label>
                <div className="flex items-center gap-2">
                  <Input readOnly value={deepLink} className="text-xs" data-testid="telegram-deeplink" />
                  <Button variant="outline" size="icon" onClick={copyLink} data-testid="telegram-copy-btn">
                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Abra o link, toque em INICIAR/START no bot e a vinculação é feita na hora.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
